import type { ReactNode } from "react";
import type { Model3dMeta } from "@agentsaas/protocol";
import { Badge } from "@agentsaas/ui";
import { useT } from "../app/i18n";

/**
 * 模型指标列表（查看器浮层与资产详情的指标面板共用）：
 * 面数、顶点、网格、材质、贴图、动画、骨骼、尺寸（米）、校验结果。
 * source：meta = 来自 AssetVersion.model（入库时解析）；computed = 查看器现场计算。
 */
export function ModelStats({ meta, source, compact }: { meta: Model3dMeta; source?: "meta" | "computed"; compact?: boolean }) {
  const { t, locale } = useT();
  const n = (x: number) => new Intl.NumberFormat(locale).format(x);
  const len = (x: number) => new Intl.NumberFormat(locale, { maximumFractionDigits: 2 }).format(x);
  const maxTex = meta.textures.reduce((m, x) => (x.width * x.height > m.width * m.height ? x : m), { width: 0, height: 0, mimeType: "" });
  const rows: { key: string; label: string; value: ReactNode; tone?: "danger" | "warning" }[] = [
    { key: "format", label: t("model3d.stats.format"), value: meta.format.toUpperCase() },
    { key: "tris", label: t("model3d.stats.tris"), value: n(meta.tris) },
    { key: "verts", label: t("model3d.stats.verts"), value: n(meta.verts) },
    { key: "meshes", label: t("model3d.stats.meshes"), value: n(meta.meshes) },
    { key: "materials", label: t("model3d.stats.materials"), value: n(meta.materials) },
    {
      key: "textures",
      label: t("model3d.stats.textures"),
      value: meta.textures.length ? t("model3d.stats.textures_value", { count: meta.textures.length, size: maxTex.width ? `${maxTex.width}×${maxTex.height}` : "?" }) : t("model3d.stats.none"),
    },
    { key: "animations", label: t("model3d.stats.animations"), value: meta.animations.length ? meta.animations.join(", ") : t("model3d.stats.none") },
    { key: "skeleton", label: t("model3d.stats.skeleton"), value: meta.skinned ? t("model3d.stats.joints", { count: meta.joints }) : t("model3d.stats.none") },
    { key: "bbox", label: t("model3d.stats.bbox"), value: t("model3d.stats.bbox_value", { x: len(meta.bbox.x), y: len(meta.bbox.y), z: len(meta.bbox.z) }) },
    {
      key: "validation",
      label: t("model3d.stats.validation"),
      value: meta.validation ? t("model3d.stats.validation_value", { errors: meta.validation.errors, warnings: meta.validation.warnings }) : t("model3d.stats.not_validated"),
      tone: meta.validation?.errors ? "danger" : meta.validation?.warnings ? "warning" : undefined,
    },
  ];
  return (
    <div className={`mstats${compact ? " mstats--compact" : ""}`} data-testid="model-stats" data-source={source}>
      {source ? <div className="mstats__source small muted">{t(source === "meta" ? "model3d.stats.source_meta" : "model3d.stats.source_computed")}</div> : null}
      <dl className="mstats__list">
        {rows.map((r) => (
          <div key={r.key} className="mstats__row" data-testid={`mstat-${r.key}`}>
            <dt>{r.label}</dt>
            <dd>{r.tone ? <Badge tone={r.tone}>{r.value}</Badge> : r.value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}
